/**
 * Fine Goods — curated collection page behavior.
 *
 * 1. product_card_click tracking: fires when a tracked card click is
 *    actually navigating (not the first tap of the mobile two-tap
 *    preview, which calls preventDefault). Pushes to window.dataLayer
 *    and, when the Meta Pixel is present, a trackCustom event with
 *    product handle, grid position and product family.
 *
 * 2. Compact sort & filter control (details.fg-controls): sorts the
 *    cards of every family grid client-side by price or restores the
 *    curated order, and can hide out-of-stock cards. 23 products —
 *    no server round-trips needed.
 */

// --- Tracking -------------------------------------------------------
// Every event fans out to whichever sinks exist: a DOM CustomEvent
// (debuggable, extensible), Shopify web pixels, GTM's dataLayer, and
// the Meta Pixel (this store's primary traffic source).

function emit(name, payload) {
  try {
    document.dispatchEvent(new CustomEvent(`fg:${name}`, { detail: payload }));
  } catch {}
  try {
    window.Shopify?.analytics?.publish?.(name, payload);
  } catch {}
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...payload });
  } catch {}
  try {
    if (typeof window.fbq === 'function') window.fbq('trackCustom', name, payload);
  } catch {}
}

document.addEventListener('click', (event) => {
  if (event.defaultPrevented) return;
  if (!(event.target instanceof Element)) return;
  const card = event.target.closest('[data-fg-track]');
  if (!card) return;

  emit('product_card_click', {
    product_handle: card.getAttribute('data-fg-handle') || '',
    grid_position: Number(card.getAttribute('data-fg-position')) || 0,
    product_family: card.getAttribute('data-fg-family') || '',
    list: card.getAttribute('data-fg-list') || '',
  });
});

// Scroll depth (25/50/75/100, once each) — only on pages that carry
// the curated grids, to help read click-through against exposure.
// Skipped when the standalone fg-collection-analytics script already
// tracks scroll depth on this page.
if (!window.__fgCollectionAnalytics && document.querySelector('[data-fg-grid]')) {
  const seen = new Set();
  const onScroll = () => {
    const doc = document.documentElement;
    const max = doc.scrollHeight - window.innerHeight;
    if (max <= 0) return;
    const pct = Math.round((window.scrollY / max) * 100);
    for (const threshold of [25, 50, 75, 100]) {
      if (pct >= threshold && !seen.has(threshold)) {
        seen.add(threshold);
        emit('scroll_depth', { percent: threshold });
      }
    }
    if (seen.size === 4) window.removeEventListener('scroll', onScroll);
  };
  window.addEventListener('scroll', onScroll, { passive: true });
}

// --- Compact sort & filter ------------------------------------------

function grids() {
  return [...document.querySelectorAll('[data-fg-grid]')];
}

function cardsOf(grid) {
  return [...grid.querySelectorAll('.fg-card')];
}

// Remember the curated order once.
for (const grid of grids()) {
  cardsOf(grid).forEach((card, i) => {
    card.dataset.fgCurated = String(i);
  });
}

function applySort(mode) {
  for (const grid of grids()) {
    const sorted = cardsOf(grid).sort((a, b) => {
      if (mode === 'price-asc') return Number(a.dataset.fgPrice) - Number(b.dataset.fgPrice);
      if (mode === 'price-desc') return Number(b.dataset.fgPrice) - Number(a.dataset.fgPrice);
      return Number(a.dataset.fgCurated) - Number(b.dataset.fgCurated);
    });
    for (const card of sorted) grid.appendChild(card);

    // Keep the editorial break after its configured card position,
    // whatever the current order is.
    const brk = grid.querySelector('.fg-family__break');
    if (brk) {
      const n = Number(grid.getAttribute('data-fg-break-after')) || 0;
      const anchor = n > 0 ? sorted[n - 1] : null;
      if (anchor) anchor.after(brk);
    }
  }
}

function applyStock(onlyInStock) {
  for (const grid of grids()) {
    for (const card of cardsOf(grid)) {
      card.hidden = onlyInStock && card.dataset.fgAvailable === 'false';
    }
  }
}

const control = document.querySelector('[data-fg-controls]');
if (control) {
  const buttons = [...control.querySelectorAll('[data-fg-sort]')];
  for (const button of buttons) {
    button.addEventListener('click', () => {
      for (const b of buttons) b.setAttribute('aria-pressed', String(b === button));
      applySort(button.getAttribute('data-fg-sort'));
    });
  }

  const stock = control.querySelector('[data-fg-instock]');
  stock?.addEventListener('change', () => applyStock(stock.checked));

  // Close the popover when clicking outside it.
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    if (control.hasAttribute('open') && !control.contains(event.target)) {
      control.removeAttribute('open');
    }
  });
}
